import React from "react";
import { Button, ButtonVariant, TextField } from "@merge-api/merge-javascript-shared";
import { showErrorToast, showSuccessToast } from "../../../../../shared/Toasts";
import { FormErrorData, fetchWithAuth } from "../../../../../../api-client/APIClient";
import { SelectedWebhookType } from "../enums";
import styled from "styled-components";

interface WebhookUrlInputProps {
  webhookUrlError: string | undefined;
  setWebhookUrlError: (webhookUrlError: string | undefined) => void;
  displayTarget?: string;
  target?: string;
  onTargetChange: (target: string) => void;
  selectedWebhookType: Set<SelectedWebhookType>;
  trigger: (name?: string | string[] | undefined) => Promise<boolean>;
  selectedEvents: string[];
  isLoading: boolean;
}

const OverrideHeightSmallWhiteButton = styled(Button)`
  &&& {
    height: 40px !important;
  }
`;

function WebhookUrlInput(props: WebhookUrlInputProps) {
  const {
    isLoading,
    webhookUrlError,
    setWebhookUrlError,
    onTargetChange,
    displayTarget,
    target,
    selectedEvents,
    trigger,
    selectedWebhookType,
  } = props;

  // event handlers
  const onTestClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Trigger form validation to make errors go away if they have been fixed.
    const formIsValid = await trigger();
    if (!formIsValid) {
      return;
    }

    if (selectedWebhookType.size === 0) {
      showErrorToast("Please select a webhook trigger.");
      return;
    }

    const formData = {
      target,
      event:
        selectedEvents.length > 0 && selectedEvents[0] !== "all"
          ? selectedEvents[0]
          : "Candidate.added",
    };

    fetchWithAuth({
      path: "/integrations/webhooks/test",
      method: "POST",
      body: formData,
      onResponse: (data) => {
        if (data.status_code < 300) {
          showSuccessToast(data.message);
          setWebhookUrlError(undefined);
        } else {
          showErrorToast(data.message);
        }
      },
      onError: (err: Response | undefined) => {
        if (err) {
          err.json().then((data: FormErrorData) => {
            for (const field_name in data) {
              if (field_name === "non_field_errors") {
                showErrorToast(data[field_name][0]);
                continue;
              }
              if (field_name == "target") {
                setWebhookUrlError(data[field_name][0]);
              }
            }
          });
        } else {
          showErrorToast("Something went wrong, please check your selections and try again.");
        }
      },
    });
  };

  return (
    <>
      <h5 className="mb-4">Webhook URL</h5>
      <div className="flex flex-row flex-nowrap w-full">
        <TextField
          className="w-full"
          name="target"
          prefix="https://"
          prefixVariant="url"
          placeholder={isLoading ? "loading..." : "www.example.com/receive-payload"}
          value={displayTarget}
          onChange={(e) => {
            onTargetChange(e.target.value);
            setWebhookUrlError(undefined);
          }}
          error={!!webhookUrlError}
          errorText={webhookUrlError}
        />
        <OverrideHeightSmallWhiteButton
          size="sm"
          variant={ButtonVariant.TertiaryWhite}
          onClick={onTestClick}
          className="!h-10 ml-3"
          disabled={!displayTarget}
        >
          Send test POST request
        </OverrideHeightSmallWhiteButton>
      </div>
    </>
  );
}

export default WebhookUrlInput;
